import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ComingSoon from "../pages/ComingSoon";

/**
 * LaunchGate — global flag wrapper.
 *
 * If Coming Soon Mode is ON and the current user is NOT an admin, we render
 * the Coming Soon landing page. Otherwise we render the full app (children).
 *
 * Always-allowed paths (needed so admins can still authenticate):
 *   /login, /register, /forgot-password, /reset-password,
 *   /verify-email/*, /admin/*, and the Emergent OAuth callback hash.
 *
 * Zero impact when Coming Soon Mode is OFF — the flag returns `false` in one
 * fast lookup and children render as before.
 */
const ALWAYS_ALLOW_PREFIXES = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/admin",
];

export default function LaunchGate({ children }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [checking, setChecking] = useState(true);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/api/launch/config");
      setConfig(data);
    } catch (e) {
      // If the flag endpoint fails, do NOT block the app — fall through to normal routes
      setConfig({ coming_soon_enabled: false });
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Wait for both the auth check and the flag check before deciding.
  if (checking || authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white/40 text-[11px] font-mono tracking-[0.2em] uppercase">Loading…</div>
      </div>
    );
  }

  const isAdmin = user?.role === "admin";
  const gateOn = !!config?.coming_soon_enabled;
  const path = location?.pathname || "/";

  // Never gate authentication / verification / admin routes — otherwise the
  // owner can't log in when Coming Soon Mode is on.
  const isAllowedPath = ALWAYS_ALLOW_PREFIXES.some((p) =>
    path === p || path.startsWith(p + "/") || path.startsWith(p + "?")
  );

  // Preview override — admin can preview the coming-soon page anytime via `?preview=coming-soon`
  // and preview the live app via `?preview=live`.
  let previewOverride = null;
  if (typeof window !== "undefined" && isAdmin) {
    const p = new URLSearchParams(window.location.search).get("preview");
    if (p === "coming-soon") previewOverride = "coming-soon";
    if (p === "live") previewOverride = "live";
  }

  const showComingSoon =
    !isAllowedPath &&
    (previewOverride === "coming-soon" ||
      (previewOverride !== "live" && gateOn && !isAdmin));

  if (showComingSoon) {
    return (
      <ComingSoon
        launchAt={config?.launch_at}
        counter={config?.counter}
        onLaunched={loadConfig}
      />
    );
  }
  return children;
}
